import cx from 'classnames';

import { Content } from '../../../models/content/types';
import ContentCardPlaceholder from '../../../components/placeholders/content-card-placeholder';
import FeedHeader, { FeedHeaderProps, FeedViewAllFooter } from './feed-header';

import './content-layouts.scss';
import {
  FeedCardWithData,
  FeedCardProps,
} from '../../content/solar-feed-card/solar-feed-card';
import {
  FeedCardGrid,
  FeedCardGridItem,
} from '@socialchorus/shared-ui-components';
import useScreenSize from '../../../common/use-screen-size';
import useFeatureFlag from '../../../common/use-feature-flag';
import { Feature } from '../../../models/features/features';
import { MobileHorizontalFeedCards } from './horizontal-scroll-row';

interface IGridLayoutList {
  contents: Content[];
  limit: number;
  analyticsData: FeedCardProps['analyticsData'];
  title?: string;
}

const GridLayoutList = ({
  contents,
  limit,
  analyticsData,
  title,
}: IGridLayoutList) => {
  const { isMobile } = useScreenSize();
  const mobileWebParity = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_PARITY
  );

  if (isMobile && mobileWebParity) {
    return (
      <MobileHorizontalFeedCards
        contents={contents}
        analyticsData={analyticsData}
        title={title}
      />
    );
  }

  contents = limit ? contents.slice(0, limit) : contents;

  return (
    <FeedCardGrid columns={isMobile ? 1 : 2}>
      {contents.map((content) => (
        <FeedCardGridItem key={content.id}>
          <FeedCardWithData
            content={content}
            analyticsData={analyticsData}
            compact
            fullHeight
            layout="horizontal"
            titleHeadingLevel={2}
          />
        </FeedCardGridItem>
      ))}
    </FeedCardGrid>
  );
};

type ContentLayoutGridProps = FeedHeaderProps & {
  description?: string;
  viewAllText: string;
  feedUrl: string;
  contents: any;
  minLength: number;
  maxLength: number;
  isFetching: boolean;
  isTopic: boolean;
};

const ContentLayoutGrid = ({
  id,
  title,
  description,
  viewAllText,
  feedUrl,
  contents,
  minLength,
  maxLength,
  isFetching,
  analyticsData,
  isTopic = false,
}: ContentLayoutGridProps) => {
  const { isMobile } = useScreenSize();
  const mobileWebParity = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_PARITY
  );
  const isMobileHorizontal = isMobile && !!mobileWebParity;

  const className = cx('home-filtered-feed', 'grid-layout-bkgd', {
    'home-filtered-feed--loading': isFetching,
  });

  if (!isFetching && contents.length < minLength) return null;

  return (
    <div className={className}>
      <FeedHeader
        id={id}
        title={title}
        description={description}
        viewAllLink={feedUrl}
        viewAllText={viewAllText}
        isTopic={isTopic}
        analyticsData={analyticsData}
        isMobileHorizontal={isMobileHorizontal}
      />
      {isFetching ? (
        <div className="content-list content-list--landscape">
          {[...Array(maxLength)].map((_, idx) => (
            <ContentCardPlaceholder key={idx} />
          ))}
        </div>
      ) : (
        <GridLayoutList
          contents={contents}
          limit={maxLength}
          analyticsData={analyticsData}
          title={title}
        />
      )}
      {isMobileHorizontal && feedUrl && (
        <FeedViewAllFooter
          viewAllLink={feedUrl}
          viewAllText={viewAllText}
          isTopic={isTopic}
          analyticsData={analyticsData}
        />
      )}
    </div>
  );
};

export default ContentLayoutGrid;
